// AuthProvider + useAuth hook.
// Ports vanilla's onAuthStateChanged listener (public/app/index.html L462).
// Tracks user + emailVerified + loading flag.
//
// Partner access (V1 parity, public/app/index.html:19335 togglePartnerAccess):
// When the signed-in user's email is in the team doc's elevatedMembers list,
// `effectiveUid` switches to the team creator's uid. All hooks that read/write
// appstate documents (expenses, projects, bids, etc.) route through
// effectiveUid so a Partner sees the account holder's data, not their own.
// Resolution path mirrors V1:
//   shared/uid__{myUid}     → lookup { teamCode }
//   shared/team__{teamCode} → if myEmail in elevatedMembers → createdBy

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { User } from 'firebase/auth'
import { getDoc } from 'firebase/firestore'
import { auth, onAuthStateChanged } from '../lib/firebase/auth'
import { sharedDoc } from '../lib/firebase/firestore'
import { setSentryUser, breadcrumb } from '../lib/sentry'

interface AuthContextValue {
  /** Firebase user or null when signed out. */
  user: User | null
  /** True until the first onAuthStateChanged callback fires. */
  loading: boolean
  /** Convenience: user is signed in AND has verified email. */
  isReady: boolean
  /** Current user's uid, or null. */
  uid: string | null
  /** uid that owns the appstate this user should read/write. Equal to `uid`
   *  for admins + non-elevated members; set to the account holder's uid
   *  when the signed-in user is a Partner (in team.elevatedMembers). */
  effectiveUid: string | null
  /** True when the effective-uid resolver has completed (even if the
   *  answer is "same as own uid"). Prevents a Partner from momentarily
   *  reading their own empty appstate on first paint. */
  effectiveReady: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

async function resolveEffectiveUid(user: User): Promise<string> {
  const myUid = user.uid
  const myEmail = (user.email || '').toLowerCase()
  if (!myEmail) return myUid
  try {
    const lookup = await getDoc(sharedDoc(`uid__${myUid}`))
    const teamCode = lookup.exists() ? ((lookup.data() as { teamCode?: string }).teamCode || '') : ''
    if (!teamCode) return myUid
    const teamSnap = await getDoc(sharedDoc(`team__${teamCode}`))
    if (!teamSnap.exists()) return myUid
    const teamDoc = teamSnap.data() as { elevatedMembers?: string[]; createdBy?: string }
    const elevated = Array.isArray(teamDoc.elevatedMembers)
      ? teamDoc.elevatedMembers.map((e) => (e || '').toLowerCase())
      : []
    if (teamDoc.createdBy && teamDoc.createdBy !== myUid && elevated.includes(myEmail)) {
      return teamDoc.createdBy
    }
  } catch (err) {
    console.warn('[auth] effective-uid resolve failed, falling back to own uid', err)
  }
  return myUid
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(auth.currentUser)
  const [loading, setLoading] = useState(true)
  const [effectiveUid, setEffectiveUid] = useState<string | null>(auth.currentUser?.uid ?? null)
  const [effectiveReady, setEffectiveReady] = useState(false)
  const firstFire = useRef(true)

  useEffect(() => {
    const unsub = onAuthStateChanged((u) => {
      setUser(u)
      if (firstFire.current) {
        firstFire.current = false
        setLoading(false)
      }
      breadcrumb('auth', u ? 'user signed in' : 'user signed out', { uid: u?.uid })
      setSentryUser(u, u ? 'admin' : 'field_crew')
      if (!u) {
        setEffectiveUid(null)
        setEffectiveReady(true)
        return
      }
      // Default to own uid until the async lookup resolves — admins + regular
      // PMs stay on their own uid anyway, so this is correct for the common case.
      setEffectiveUid(u.uid)
      setEffectiveReady(false)
      void resolveEffectiveUid(u).then((eff) => {
        setEffectiveUid(eff)
        setEffectiveReady(true)
        if (eff !== u.uid) {
          breadcrumb('auth', 'partner access active', { ownerUid: eff })
        }
      })
    })
    return unsub
  }, [])

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    isReady: !!user && user.emailVerified,
    uid: user?.uid ?? null,
    effectiveUid,
    effectiveReady,
  }), [user, loading, effectiveUid, effectiveReady])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}

/** Hook returns the required uid or throws. Convenience for pages that are
 *  already gated by <RequireAuth>. */
export function useUid(): string {
  const { uid } = useAuth()
  if (!uid) throw new Error('useUid called without an authenticated user')
  return uid
}

/** Returns the uid that owns this user's appstate data. Equal to own uid
 *  for admins + non-elevated members; equal to the team creator's uid for
 *  Partners. Throws if the user isn't authenticated. */
export function useEffectiveUid(): string {
  const { effectiveUid } = useAuth()
  if (!effectiveUid) throw new Error('useEffectiveUid called without an authenticated user')
  return effectiveUid
}
